import { useMemo } from 'react';
import { PrestationAvecPrix } from '../types';

interface UseCalculationsProps {
  prestations: PrestationAvecPrix[];
  selectedSuppliers: string[];
  quantites: Record<string, number>;
}

export const useCalculations = ({
  prestations,
  selectedSuppliers,
  quantites,
}: UseCalculationsProps) => {
  // Calculer l'économie réalisée : somme des MAX - somme des MIN
  const economieRealisee = useMemo(() => {
    let sommeMax = 0;
    let sommeMin = 0;

    prestations.forEach((prestation) => {
      const prixTotaux = selectedSuppliers
        .map((fournisseur) => {
          const prixFournisseur = prestation.prixParFournisseur.find(
            (p) => p.fournisseur === fournisseur
          );
          if (!prixFournisseur) return null;
          const quantite = quantites[prestation.id] ?? prestation.quantite;
          return quantite * prixFournisseur.prixUnitaire;
        })
        .filter((prix): prix is number => prix !== null);

      if (prixTotaux.length > 0) {
        const max = Math.max(...prixTotaux);
        const min = Math.min(...prixTotaux);
        sommeMax += max;
        sommeMin += min;
      }
    });

    return sommeMax - sommeMin;
  }, [prestations, selectedSuppliers, quantites]);

  // Écarts types prestations : écart en valeur de toutes les lignes qui existent dans tous les fournisseurs sélectionnés
  const ecartsTypesPrestations = useMemo(() => {
    let sommeEcarts = 0;

    prestations.forEach((prestation) => {
      const prixTotaux = selectedSuppliers
        .map((fournisseur) => {
          const prixFournisseur = prestation.prixParFournisseur.find(
            (p) => p.fournisseur === fournisseur
          );
          if (!prixFournisseur) return null;
          const quantite = quantites[prestation.id] ?? prestation.quantite;
          return quantite * prixFournisseur.prixUnitaire;
        })
        .filter((prix): prix is number => prix !== null);

      // Seulement si la prestation existe dans TOUS les fournisseurs sélectionnés
      if (prixTotaux.length === selectedSuppliers.length && prixTotaux.length > 0) {
        const max = Math.max(...prixTotaux);
        const min = Math.min(...prixTotaux);
        sommeEcarts += max - min;
      }
    });

    return sommeEcarts;
  }, [prestations, selectedSuppliers, quantites]);

  // Écart-types fournisseurs : écart minimum et maximum entre les fournisseurs sur les prestations où tous les fournisseurs ont proposé un prix
  const ecartsTypesFournisseurs = useMemo(() => {
    const ecarts: number[] = [];

    prestations.forEach((prestation) => {
      const prixTotaux = selectedSuppliers
        .map((fournisseur) => {
          const prixFournisseur = prestation.prixParFournisseur.find(
            (p) => p.fournisseur === fournisseur
          );
          if (!prixFournisseur) return null;
          const quantite = quantites[prestation.id] ?? prestation.quantite;
          return quantite * prixFournisseur.prixUnitaire;
        })
        .filter((prix): prix is number => prix !== null);

      // Seulement si la prestation existe dans TOUS les fournisseurs sélectionnés
      if (prixTotaux.length === selectedSuppliers.length && prixTotaux.length > 0) {
        const max = Math.max(...prixTotaux);
        const min = Math.min(...prixTotaux);
        ecarts.push(max - min);
      }
    });

    if (ecarts.length === 0) {
      return { min: 0, max: 0 };
    }

    return {
      min: Math.min(...ecarts),
      max: Math.max(...ecarts),
    };
  }, [prestations, selectedSuppliers, quantites]);

  // Écart-types potentiels : écart minimum et maximum possible sur les prestations où au moins un fournisseur a proposé un prix
  const ecartsTypesPotentiels = useMemo(() => {
    const ecarts: number[] = [];

    prestations.forEach((prestation) => {
      const prixTotaux = selectedSuppliers
        .map((fournisseur) => {
          const prixFournisseur = prestation.prixParFournisseur.find(
            (p) => p.fournisseur === fournisseur
          );
          if (!prixFournisseur) return null;
          const quantite = quantites[prestation.id] ?? prestation.quantite;
          return quantite * prixFournisseur.prixUnitaire;
        })
        .filter((prix): prix is number => prix !== null);

      // Si au moins un fournisseur a un prix
      if (prixTotaux.length > 0) {
        const max = Math.max(...prixTotaux);
        const min = Math.min(...prixTotaux);
        ecarts.push(max - min);
      }
    });

    if (ecarts.length === 0) {
      return { min: 0, max: 0 };
    }

    return {
      min: Math.min(...ecarts),
      max: Math.max(...ecarts),
    };
  }, [prestations, selectedSuppliers, quantites]);

  return {
    economieRealisee,
    ecartsTypesPrestations,
    ecartsTypesFournisseurs,
    ecartsTypesPotentiels,
  };
};

